import useCustomer from '../commerce-api/hooks/useCustomer'
import {useCommerceAPI} from '../commerce-api/contexts'
import {useEffect, useState} from 'react'
import {isProductSet, isProductVariant} from '../utils/product-utils'

/**
 * This hook is used to fetch product customer based promotions when product was loaded on server.
 *
 * On server the products are loaded with guest customer promotions, but when the customer is registered
 * we need to fetch the promotions for the registered customer, as they might be different.
 *
 * Note: while the customer promotions are loading the original product with guest promotions is returned.
 *
 * @param {object} product
 * @param {boolean} loadedOnServer
 * @returns {[object,boolean]} - product with customer promotions and loading state
 */
const useProductWithCustomerPromotions = (product, loadedOnServer) => {
    const api = useCommerceAPI()
    const customer = useCustomer()

    // only variant product or product sets loaded on server when we have a registered customer need to be updated
    const needsFetchingCustomerPromotions =
        (product && customer?.isRegistered && loadedOnServer && isProductSet(product)) ||
        isProductVariant(product)

    const [productWithCustomerPromotions, setProductWithCustomerPromotions] = useState(product)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        // immediately update with the changed product
        setProductWithCustomerPromotions(product)
        setLoading(needsFetchingCustomerPromotions)

        if (!needsFetchingCustomerPromotions) {
            return
        }

        let staleRequest = false
        if (isProductSet(product)) {
            const selectedSetProductIds = product.setProducts.map((sp) => sp.id)
            Promise.all([
                api.customObjects.getProductSetPrice({
                    parameters: {
                        id: product.id,
                        selectedSetProducts: selectedSetProductIds
                    }
                }),
                api.shopperProducts.getProducts({
                    parameters: {
                        ids: selectedSetProductIds.join(','),
                        // minimize response to only promotions
                        allImages: false,
                        expand: ['prices', 'promotions'].join(','),
                        select: '(data.(id,productPromotions.(**)))'
                    }
                })
            ]).then(([priceResp, productsResp]) => {
                if (staleRequest) {
                    return
                }

                const productPromotions = Object.fromEntries(
                    productsResp.data.map((p) => [p.id, p])
                )
                setProductWithCustomerPromotions((p) => ({
                    ...p,
                    ...priceResp,
                    setProducts: p.setProducts.map((sp) => {
                        if (!productPromotions[sp.id]?.productPromotions) {
                            console.warn(`Set product ${sp.id} promotions not found`)
                            // can return the original set product to reduce re-renders
                            return sp
                        }
                        return {
                            ...sp,
                            ...(productPromotions[sp.id]?.productPromotions
                                ? {productPromotions: productPromotions[sp.id].productPromotions}
                                : {})
                        }
                    })
                }))
            })
        } else if (isProductVariant(product)) {
            api.shopperProducts
                .getProduct({
                    parameters: {
                        id: product.id,
                        // minimize response to only promotions
                        allImages: false,
                        expand: ['prices', 'promotions'].join(','),
                        select: '(productPromotions.(**))'
                    }
                })
                .then(
                    (data) => {
                        if (staleRequest) {
                            return
                        }
                        if (!data.productPromotions) {
                            console.warn('Product promotions not found')
                            return
                        }
                        setProductWithCustomerPromotions((p) => ({
                            ...p,
                            productPromotions: data.productPromotions
                        }))
                    },
                    (error) => {
                        console.error('Error fetching product promotions', error)
                    }
                )
                .finally(() => {
                    if (staleRequest) {
                        return
                    }
                    setLoading(false)
                })
        } else {
            console.warn('Product is not a variant or set product', product?.type)
            setLoading(false)
        }

        return () => {
            staleRequest = true
        }
    }, [api, product, needsFetchingCustomerPromotions, customer])

    return [productWithCustomerPromotions, loading]
}

export default useProductWithCustomerPromotions
